import merge from 'lodash/merge'
import { ChartCandle } from '../chart.typings'
import BaseChartSeries from './baseSeries'
import { DeepPartial } from 'lightweight-charts'
import { defaultPandFOptions, PandFSeriesOptions } from '../customSeries/p&FSeries/options'
import { PandFSeries as CustomPandFSeries } from '../customSeries/p&FSeries/p&f-series'
import { atr } from '../../../util/IndicatorUtils/core/indicators'
import { PandFData } from '../customSeries/p&FSeries/data'

class PandFSeries extends BaseChartSeries<PandFData, PandFSeriesOptions> {
    customPaneView?: CustomPandFSeries<PandFData>
    brickSize: number = 0

    init() {
        this.customPaneView = new CustomPandFSeries();
    }

    addSeriesToChart() {
        return this.createCustomSeries(this.customPaneView!, this.seriesOptions)
    }

    getDefaultOptions(): PandFSeriesOptions {
        return merge(defaultPandFOptions, this.options)
    }

    getPrimaryData(length = 0): ChartCandle[] {
        return length > 0 ? this.data.slice(-length) : this.data;
    }

    onOptionsUpdate(options: DeepPartial<PandFSeriesOptions>): void {
        const updateKeys = ["boxMethod", "boxSize", "atrLength", "percentage", "reversalAmount"] as (keyof PandFSeriesOptions)[]
        const needsUpdate = updateKeys.some(key => key in options)

        if (needsUpdate) {
            this.brickSize = 0
            this._setData(this.rawData);
            this.chart.refreshNonPrimaryGroups();
            this.chart.checkIfDataRequired();
        }
    }

    generateSeriesData(data: ChartCandle[], metadata: Record<string, any> = {}, update = false): PandFData[] {
        const rawData = this.rawData;
        if (rawData.length === 0) return []
        
        if (!this.brickSize) {
            this.brickSize = this.computeBoxSize(rawData)
        }
        const boxSize = this.brickSize
        const reversalAmount = this.seriesOptions.reversalAmount ?? defaultPandFOptions.reversalAmount
        
        if (rawData.length === 1) {
            const candle = rawData[0]
            const startingOpen = candle.close >= candle.open ? candle.close - boxSize : candle.close + boxSize
            return [{
                ...candle,
                open: startingOpen,
                close: candle.close,
                high: Math.max(startingOpen, candle.close),
                low: Math.min(startingOpen, candle.close),
                barCount: Math.max(1, Math.floor(Math.abs(startingOpen - candle.close) / boxSize)),
                volume: candle.volume
            }]
        }

        if (update) {
            if (this.data.length === 0) return this.data

            const lastPandF = this.data[this.data.length - 1]
            const [currentCandle] = data.slice(-1)
            const prevUp = lastPandF.close >= lastPandF.open

            const prevail = (prevUp && currentCandle.close - lastPandF.close >= boxSize) ||
                           (!prevUp && lastPandF.close - currentCandle.close >= boxSize)

            const reversalMove = prevUp 
                ? lastPandF.close - currentCandle.close 
                : currentCandle.close - lastPandF.close
            const reversal = reversalMove >= (reversalAmount * boxSize)

            if (prevail) {
                const boxesToAdd = Math.floor((prevUp ? currentCandle.close - lastPandF.close : lastPandF.close - currentCandle.close) / boxSize)
                const close = prevUp 
                    ? lastPandF.close + (boxesToAdd * boxSize) 
                    : lastPandF.close - (boxesToAdd * boxSize)

                return [{
                    ...lastPandF,
                    close: close,
                    high: Math.max(lastPandF.open, close),
                    low: Math.min(lastPandF.open, close),
                    barCount: lastPandF.barCount + boxesToAdd,
                    volume: lastPandF.volume + currentCandle.volume,
                    time: lastPandF.time
                }]
            } else if (reversal) {
                return [{
                    ...lastPandF,
                    time: lastPandF.time,
                    volume: lastPandF.volume + currentCandle.volume
                }]
            } else {
                return [{
                    ...lastPandF,
                    volume: lastPandF.volume + currentCandle.volume,
                    time: lastPandF.time
                }]
            }
        }

        const pAndFCandles: PandFData[] = []
        const firstCandle = rawData[0]
        const startingOpen = firstCandle.close >= firstCandle.open
            ? firstCandle.close - boxSize
            : firstCandle.close + boxSize

        let lastPandF: PandFData = {
            ...firstCandle,
            open: startingOpen,
            close: firstCandle.close,
            high: Math.max(startingOpen, firstCandle.close),
            low: Math.min(startingOpen, firstCandle.close),
            barCount: Math.max(1, Math.floor(Math.abs(startingOpen - firstCandle.close) / boxSize)),
            volume: firstCandle.volume
        }
        pAndFCandles.push(lastPandF)

        const reversalThreshold = reversalAmount * boxSize

        for (let i = 1; i < rawData.length; i++) {
            const currentCandle = rawData[i]
            const currentClose = currentCandle.close
            const lastClose = lastPandF.close
            const prevUp = lastClose >= lastPandF.open

            const priceMove = prevUp ? currentClose - lastClose : lastClose - currentClose
            const reversalMove = prevUp ? lastClose - currentClose : currentClose - lastClose

            if (priceMove >= boxSize) {
                const boxesToAdd = Math.floor(priceMove / boxSize)
                const newClose = prevUp 
                    ? lastClose + (boxesToAdd * boxSize) 
                    : lastClose - (boxesToAdd * boxSize)
                
                lastPandF.close = newClose
                lastPandF.high = Math.max(lastPandF.open, newClose)
                lastPandF.low = Math.min(lastPandF.open, newClose)
                lastPandF.barCount += boxesToAdd
                lastPandF.volume += currentCandle.volume
            } else if (reversalMove >= reversalThreshold) {
                const boxesToAdd = Math.floor(reversalMove / boxSize)
                const open = prevUp ? lastClose - boxSize : lastClose + boxSize
                const close = prevUp 
                    ? open - ((boxesToAdd - 1) * boxSize) 
                    : open + ((boxesToAdd - 1) * boxSize)

                lastPandF = {
                    ...currentCandle,
                    open: open,
                    close: close,
                    high: Math.max(open, close),
                    low: Math.min(open, close),
                    barCount: Math.max(1, boxesToAdd),
                    volume: currentCandle.volume
                }
                pAndFCandles.push(lastPandF)
            } else {
                lastPandF.volume += currentCandle.volume
            }
        }

        return pAndFCandles
    }

    computeBoxSize(data: ChartCandle[]): number {
        if (data.length === 0) return 1

        const { boxMethod, boxSize, atrLength, percentage } = this.seriesOptions
        const security = this.chart.securities?.[0]
        const tickSize = security?.tickSize || 5
        const priceMultiplier = security?.priceMultiplier ?? 100
        const tickSizeInPrice = tickSize / priceMultiplier

        let calculatedBoxSize: number

        switch (boxMethod) {
            case 'traditional':
                calculatedBoxSize = boxSize ?? 1
                break
            case 'percentage':
                if (percentage === undefined) {
                    throw new Error('percentage must be defined when boxMethod is "percentage"')
                }
                const [lastCandle] = data.slice(-1)
                const ltp = lastCandle.close
                calculatedBoxSize = Number((ltp * percentage / 100).toFixed(4))
                calculatedBoxSize = Math.round(calculatedBoxSize / tickSizeInPrice) * tickSizeInPrice
                
                if (calculatedBoxSize < tickSizeInPrice) {
                    calculatedBoxSize = tickSizeInPrice
                }
                break
            case 'atr':
                if (data.length < (atrLength ?? defaultPandFOptions.atrLength)) {
                    calculatedBoxSize = tickSizeInPrice
                    break
                }

                const slice = data.slice(-(atrLength ?? defaultPandFOptions.atrLength))
                const high: number[] = []
                const low: number[] = []
                const close: number[] = []

                slice.forEach(candle => {
                    high.push(candle.high)
                    low.push(candle.low)
                    close.push(candle.close)
                })

                const atrData = atr(high, low, close, atrLength ?? defaultPandFOptions.atrLength)
                const atrValue = atrData.length ? atrData[atrData.length - 1] : 1
                calculatedBoxSize = Math.round(atrValue / tickSizeInPrice) * tickSizeInPrice

                if (calculatedBoxSize < tickSizeInPrice) {
                    calculatedBoxSize = tickSizeInPrice
                }
                break
            default:
                calculatedBoxSize = 1
        }

        return Number(calculatedBoxSize.toFixed(4))
    }

    destroy(): void {
        super.destroy()
    }
}

export default PandFSeries
